import { type CSSProperties, useId } from "react";
import type { ElectionResult } from "../api";

export type RankedBarChartProps = {
  title: string;
  description: string;
  results: readonly ElectionResult[];
  isRefreshing?: boolean;
  withPortraits?: boolean;
  /** Show the title as a panel header. Off where a surrounding control row already names the contest. */
  visibleTitle?: boolean;
};

const resultStyle = (percent: number) =>
  ({
    "--result-progress": `${Math.max(0, Math.min(percent, 100))}%`,
  }) as CSSProperties;

const portraitStyle = (index: number) =>
  ({
    "--portrait-x": `${(index % 3) * 50}%`,
    "--portrait-y": `${Math.floor(index / 3) * 100}%`,
  }) as CSSProperties;

export const RankedBarChart = ({
  title,
  description,
  results,
  isRefreshing = false,
  withPortraits = true,
  visibleTitle = false,
}: RankedBarChartProps) => {
  const titleId = useId();

  return (
    <figure
      className="ranked-chart"
      aria-labelledby={titleId}
      data-with-portraits={withPortraits || undefined}
    >
      <figcaption className={visibleTitle ? "ranked-chart__caption" : "sr-only"}>
        <h2 id={titleId}>{title}</h2>
        <p className={visibleTitle ? "sr-only" : ""}>{description}</p>
      </figcaption>

      <ul className="chart-legend sr-only" aria-label="Result series legend">
        {results.map((result) => (
          <li key={result.candidateCode}>
            <span className={`series-swatch series-color-${result.colorSlot}`} aria-hidden="true" />
            <span>{result.candidateName}</span>
          </li>
        ))}
      </ul>

      <div className="result-table-heading" aria-hidden="true">
        <span>Candidate</span>
        <span>Percent</span>
        <span>Votes</span>
      </div>

      <section
        className={isRefreshing ? "ranked-chart__plot is-refreshing" : "ranked-chart__plot"}
        aria-busy={isRefreshing}
        aria-label={`${title} results`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: Focus lets keyboard users scroll the capped ranking.
        tabIndex={0}
      >
        {results.map((result, index) => {
          const exactLabel = `${result.candidateName}, ${result.votesAmount.toLocaleString()} votes, ${result.votePercentage.toFixed(1)} percent`;
          return (
            <div className="result-row" key={result.candidateCode}>
              <span
                className={`result-row__rank series-color-${result.colorSlot}`}
                aria-hidden="true"
              >
                {index + 1}
              </span>
              {withPortraits ? (
                <span
                  className="candidate-portrait"
                  style={portraitStyle(index)}
                  aria-hidden="true"
                />
              ) : null}
              <article
                className={`result-row__content series-color-${result.colorSlot}`}
                style={resultStyle(result.votePercentage)}
                aria-label={exactLabel}
              >
                <span className="result-row__progress" aria-hidden="true" />
                <span className="result-row__name">
                  <strong>{result.candidateName}</strong>
                  <small>{result.partyAlias}</small>
                </span>
                <strong className="result-row__percent">{result.votePercentage.toFixed(1)}%</strong>
                <strong className="result-row__votes">{result.votesAmount.toLocaleString()}</strong>
              </article>
            </div>
          );
        })}
      </section>

      <details className="chart-table-view sr-only">
        <summary>Open accessible result table</summary>
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: Focus lets keyboard users scroll the overflowed table. */}
        <section className="data-table-scroll" aria-label={`${title} table`} tabIndex={0}>
          <table className="data-table">
            <caption>{title} — exact displayed totals</caption>
            <thead>
              <tr>
                <th scope="col">Rank</th>
                <th scope="col">Candidate or group</th>
                <th scope="col">Affiliation</th>
                <th scope="col" className="data-table__numeric">
                  Share
                </th>
                <th scope="col" className="data-table__numeric">
                  Votes
                </th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr key={result.candidateCode}>
                  <td>{index + 1}</td>
                  <td>{result.candidateName}</td>
                  <td>{result.partyAlias}</td>
                  <td className="data-table__numeric">{result.votePercentage.toFixed(1)}%</td>
                  <td className="data-table__numeric">{result.votesAmount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </details>
    </figure>
  );
};
